import { forwardRef } from 'react'

import { cn } from '../../../utils/cn'
import { Label } from '../../atoms/label'

import { FormFieldContext } from './form-field-context'

export interface FormFieldProps {
  /** Field label text. */
  label: string
  /** Used for the label's `htmlFor` — pass the same value as `id` on the control. */
  htmlFor: string
  /** Hides the visible label while preserving the control's accessible name. */
  showLabel?: boolean
  /** Tooltip trigger composed with Acqua's Tooltip and TooltipTrigger. */
  tooltip?: React.ReactNode
  /** Disables nested Acqua Inputs. Other controls can supply their own disabled prop. */
  disabled?: boolean
  /** Shows a red `*` next to the label. Mutually exclusive with `optional`. */
  required?: boolean
  /** Shows `(optional)` next to the label. Mutually exclusive with `required`. */
  optional?: boolean
  /** Toggles label + message colors to the error state. */
  error?: boolean
  /** Helper text shown below the control when `error` is false. */
  helper?: string
  /** Message shown below the control when `error` is true. Falls back to `helper` if omitted. */
  errorMessage?: string
  /** The control rendered between the label and the message (e.g. Input). */
  children: React.ReactNode
  className?: string
}

export const FormField = forwardRef<HTMLDivElement, FormFieldProps>(
  (
    {
      label,
      htmlFor,
      showLabel = true,
      tooltip,
      disabled,
      required,
      optional,
      error = false,
      helper,
      errorMessage,
      children,
      className
    },
    ref
  ) => {
    const message = error ? errorMessage ?? helper : helper
    const messageId = message ? `${htmlFor}-message` : undefined

    return (
      <FormFieldContext.Provider
        value={{ htmlFor, messageId, error, required, disabled }}
      >
        <div
          ref={ref}
          data-slot='form-field'
          data-error={error ? '' : undefined}
          data-disabled={disabled ? '' : undefined}
          className={cn(
            'group/form-field font-body flex min-w-0 flex-col gap-1',
            className
          )}
        >
          <div
            data-slot='form-field-label'
            className={cn(
              'flex items-center gap-1 text-[10px] font-semibold leading-[15px]',
              !showLabel && 'sr-only'
            )}
          >
            <Label
              htmlFor={htmlFor}
              weight='semibold'
              className={cn(
                'text-[10px] leading-[15px]',
                error ? 'text-danger-title' : 'text-surface-title-50',
                'group-data-[disabled]/form-field:text-surface-body group-has-[input:disabled,select:disabled,textarea:disabled]/form-field:text-surface-body'
              )}
            >
              {label}
            </Label>
            {optional && !required && (
              <span className='text-surface-body'>(optional)</span>
            )}
            {required && (
              <span aria-hidden='true' className='text-danger-icon'>
                *
              </span>
            )}
            {showLabel && tooltip && (
              <span
                data-slot='form-field-tooltip'
                className='text-surface-title-50 flex size-[15px] shrink-0 items-center justify-center text-[15px] leading-[15px]'
              >
                {tooltip}
              </span>
            )}
          </div>
          {children}
          {message && (
            <span
              id={messageId}
              data-slot='form-field-message'
              className={cn(
                'text-[10px] font-semibold leading-[15px]',
                error ? 'text-error-icon' : 'text-surface-body',
                'group-data-[disabled]/form-field:text-surface-body group-has-[input:disabled,select:disabled,textarea:disabled]/form-field:text-surface-body'
              )}
            >
              {message}
            </span>
          )}
        </div>
      </FormFieldContext.Provider>
    )
  }
)
FormField.displayName = 'FormField'
